import React from 'react';

import Bold from '@components/elements/Bold';
import MethodsSectionTitle from '@components/experiments/methods/MethodsSectionTitle';
import ProseList from '@components/ProseList';

import { AssaySummaryAnalysis } from '@models/analysis/AssaySummaryAnalysis';
import Plot from '@models/Plot';
import PieChartDisplayOption from '@models/plotDisplayOption/PieChartDisplayOption';
import StackedBarPlotDisplayOption from '@models/plotDisplayOption/StackedBarPlotDisplayOption';

import { isDefined } from '@util/TypeGuards';

type Props = { plot: Plot };
const AssaySummaryDiscreteAnalysisMethodsSection = ({ plot }: Props) => {
    const analysis = plot.analysis as AssaySummaryAnalysis;
    const display = plot.display;

    if (!analysis) {
        return <div>No analysis was found.</div>;
    }

    const hasGroups = (analysis.groups?.length ?? 0) > 0;
    const groupNames = analysis?.groups?.map((g) => g.display_name).filter(isDefined) ?? [];
    const $groups = hasGroups ? (
        <span>
            samples from the {`${analysis?.groups?.length === 1 ? 'group' : 'groups'}`}:{' '}
            <ProseList items={groupNames} />
        </span>
    ) : (
        <Bold>all samples</Bold>
    );

    return (
        <section>
            <section>
                <MethodsSectionTitle>Assay summary (discrete)</MethodsSectionTitle>
                <p className="mb-6">
                    Discrete assay data values were summarized for <ProseList items={analysis.targets} /> across{' '}
                    {$groups}.
                </p>
            </section>
            {display.display_type === 'stacked_bar_plot' ? (
                <section>
                    <MethodsSectionTitle>Stacked bar plot</MethodsSectionTitle>
                    <p className="mb-6">
                        Stacked bar plot showing the{' '}
                        <Bold>
                            {(display as StackedBarPlotDisplayOption).display_units === 'count'
                                ? 'summarized counts'
                                : 'frequency'}
                        </Bold>{' '}
                        of discrete assay data values on the y-axis for <ProseList items={analysis.targets} /> on the
                        x-axis. Bars are colored by discrete values in the assay data.
                    </p>
                </section>
            ) : null}
            {display.display_type === 'pie_chart' ? (
                <section>
                    <MethodsSectionTitle>Pie chart</MethodsSectionTitle>
                    <p className="mb-6">
                        Pie chart showing the distribution of discrete assay data values for{' '}
                        <Bold>{(display as PieChartDisplayOption).peak_set}</Bold> across {$groups}. Each slice
                        represents a discrete assay data value, with proportions corresponding to the number of samples
                        in each category.
                    </p>
                </section>
            ) : null}
        </section>
    );
};

export default AssaySummaryDiscreteAnalysisMethodsSection;
